import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SeqLabelList } from './components/seq-label-list/seq-label-list'
import { SeqMilestoneList } from './components/seq-milestone-list/seq-milestone-list'
import styles from './milestone-sequence.page.module.scss'
import { SeqWorkspace } from './seq-workspace/seq-workspace'
import { useGetDuLabels } from '../../../entities/du-labels/hooks/du-label.hooks'
import { DuLabel } from '../../../entities/du-labels/types/du-label.types'
import { useGetLabelMilestoneSeq, useGetUserWorkflows, useUpdateLabelMilestoneSeq } from '../../../entities/workflow/hooks/workflow.hooks'
import { FullMilestoneOrder, MilestoneItem, MilestoneOrder } from '../../../entities/workflow/types/workflow.types'

export const MilestoneSequencePage = () => {
  const { t } = useTranslation()

  const { data: milestones } = useGetUserWorkflows()
  const { data: labels } = useGetDuLabels()
  const { mutateAsync: updateLabelMilestoneSeq } = useUpdateLabelMilestoneSeq()
  const [checkedLabel, setCheckedLabel] = useState<DuLabel | null>(null)
  const [checkedMilestones, setCheckedMilestones] = useState<MilestoneItem[]>([])

  const { data: milestoneLabelSeq } = useGetLabelMilestoneSeq(checkedLabel?.id)

  const checkedMilestoneIds = useMemo(() => {
    return new Set(checkedMilestones.map((milestone) => milestone.id))
  }, [checkedMilestones])

  useEffect(() => {
    const checkedMilestones =
      milestoneLabelSeq?.milestoneSeq?.map((seq: MilestoneOrder) => {
        return milestones?.find((milestone) => milestone.id === seq.milestoneId)
      }) ?? []
    setCheckedMilestones([...checkedMilestones])
  }, [checkedLabel, milestoneLabelSeq, milestones])

  const handleLabelCheck = (label: DuLabel) => {
    if (checkedLabel?.id === label.id) {
      setCheckedLabel(null)

      return
    }
    setCheckedLabel(label)
  }

  const handleMilestoneCheck = async (milestone: MilestoneItem) => {
    let checkedMilestoneCopy = [...checkedMilestones]

    if (checkedMilestoneIds.has(milestone.id)) {
      checkedMilestoneCopy = checkedMilestones.filter((checkedMilestone) => checkedMilestone.id !== milestone.id)
    } else {
      checkedMilestoneCopy.push(milestone)
    }

    setCheckedMilestones(checkedMilestoneCopy)

    const milestoneOrder: MilestoneOrder[] = checkedMilestoneCopy.map((milestone, index) => ({
      milestoneId: milestone.id,
      seq: index,
    }))
    const fullMilestoneOrder: FullMilestoneOrder = {
      labelId: checkedLabel?.id ?? 0,
      milestoneOrder,
    }

    await updateLabelMilestoneSeq(fullMilestoneOrder)
  }

  const handleSeqChange = async (reorderedMilestones: MilestoneItem[]) => {
    setCheckedMilestones([...reorderedMilestones])

    const milestoneOrder: MilestoneOrder[] = reorderedMilestones.map((milestone, index) => ({
      milestoneId: milestone.id,
      seq: index,
    }))
    const fullMilestoneOrder: FullMilestoneOrder = {
      labelId: checkedLabel?.id ?? 0,
      milestoneOrder,
    }

    await updateLabelMilestoneSeq(fullMilestoneOrder)
  }

  return (
    <div className={clsx(styles.milestoneSeqWrap)}>
      <div className="col-2">
        <SeqLabelList
          labels={labels ?? []}
          onCheck={handleLabelCheck}
          selectedLabel={checkedLabel}
        />
      </div>
      {checkedLabel ? (
        <>
          <div className={clsx('col-7')}>
            {!!checkedMilestones?.length ? (
              // <CanvasRefProvider>
              //   <SeqWorkspaceFull preparedRows={milestoneRows} />
              // </CanvasRefProvider>
              <SeqWorkspace
                milestones={checkedMilestones ?? []}
                onSeqChanged={handleSeqChange}
              />
            ) : (
              <div className="col-12 h-100 d-flex justify-content-center align-items-center">{t('milestoneSequence.milestoneCheck')}</div>
            )}
          </div>
          <div className={clsx('col-3')}>
            <SeqMilestoneList
              checkedMilestoneIds={checkedMilestoneIds}
              milestones={milestones ?? []}
              onCheck={handleMilestoneCheck}
            />
          </div>
        </>
      ) : (
        <div className={clsx('col-10 d-flex justify-content-center align-items-center')}>{t('milestoneSequence.duCheck')}</div>
      )}
    </div>
  )
}
